<template>
  <div class="set">
    <!-- Usuario -->
    <section class="set-section">
      <header class="set-section__head">
        <hero-icon :icon="UserIcon" :size="14" />
        <span>Usuario</span>
      </header>

      <div class="set-section__body">
        <div v-if="error.active" class="set-error">
          <hero-icon :icon="ExclamationIcon" :size="16" />
          <span>{{ error.message }}</span>
        </div>

        <v-form v-model="valid" ref="user" lazy-validation>
          <v-row dense>
            <v-col class="py-1" cols="12" md="6">
              <div class="set-label">
                <hero-icon :icon="EnvelopeIcon" :size="13" />
                <span>E-mail</span>
              </div>
              <v-text-field
                v-model="user.email"
                :rules="emailRules"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-col>

            <v-col class="py-1" cols="12" md="6">
              <div class="set-label">
                <hero-icon :icon="LockIcon" :size="13" />
                <span>Nueva contraseña</span>
              </div>
              <v-text-field
                v-model="user.password"
                type="password"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-col>

            <v-col class="py-1" cols="12" md="6">
              <div class="set-label">
                <hero-icon :icon="KeyIcon" :size="13" />
                <span>Contraseña actual</span>
              </div>
              <v-text-field
                v-model="password"
                type="password"
                :rules="requiredRules"
                hint="Introdúcela para confirmar los cambios"
                persistent-hint
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-col>
          </v-row>

          <div class="set-footer">
            <v-btn depressed color="accent" class="rounded-lg" @click="editUser">
              Guardar
            </v-btn>
          </div>
        </v-form>
      </div>
    </section>

    <!-- Inmobiliaria (admin / gendev) -->
    <section v-if="canEditAgency" class="set-section">
      <header class="set-section__head">
        <hero-icon :icon="BuildingIcon" :size="14" />
        <span>Inmobiliaria</span>
      </header>

      <div class="set-section__body">
        <v-form v-model="valid" ref="agency" lazy-validation>
          <div class="set-logo-row">
            <div class="set-logo">
              <img
                v-if="agency.logoUrl"
                :src="imageUrl(agency.logoUrl)"
                class="set-logo__img"
                alt=""
              />
              <div v-else class="set-logo__empty">
                <hero-icon :icon="PhotoIcon" :size="24" />
                <span>Sin logo</span>
              </div>
            </div>

            <div class="set-logo-field">
              <div class="set-label">
                <hero-icon :icon="PhotoIcon" :size="13" />
                <span>Logo</span>
              </div>
              <v-file-input
                v-model="agency.logo"
                placeholder="Seleccionar imagen"
                prepend-icon=""
                prepend-inner-icon="mdi-paperclip"
                outlined
                dense
                hide-details="auto"
              ></v-file-input>
              <p class="set-hint">PNG o JPG. Se mostrará en la ficha pública.</p>
            </div>
          </div>

          <v-row dense class="mt-1">
            <v-col class="py-1" cols="12" md="6">
              <div class="set-label">
                <hero-icon :icon="IdentificationIcon" :size="13" />
                <span>Nombre</span>
              </div>
              <v-text-field
                v-model="agency.name"
                :rules="requiredRules"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-col>

            <v-col class="py-1" cols="12" md="6">
              <div class="set-label">
                <hero-icon :icon="EnvelopeIcon" :size="13" />
                <span>E-mail</span>
              </div>
              <v-text-field
                v-model="agency.email"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-col>

            <v-col class="py-1" cols="12" md="6">
              <div class="set-label">
                <hero-icon :icon="PhoneIcon" :size="13" />
                <span>Teléfono</span>
              </div>
              <v-text-field
                v-model="agency.phone"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-col>

            <v-col class="py-1" cols="12" md="6">
              <div class="set-label">
                <hero-icon :icon="GlobeIcon" :size="13" />
                <span>Sitio web</span>
              </div>
              <v-text-field
                v-model="agency.website"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-col>

            <v-col class="py-1" cols="12">
              <div class="set-label">
                <hero-icon :icon="MapPinIcon" :size="13" />
                <span>Dirección</span>
              </div>
              <v-text-field
                v-model="agency.address"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-col>
          </v-row>

          <div class="set-footer">
            <v-btn depressed color="accent" class="rounded-lg" @click="editAgency">
              Guardar
            </v-btn>
          </div>
        </v-form>
      </div>
    </section>
  </div>
</template>

<script>
import { EditUser } from '@/graphql/mutations/user'
import { EditAgency } from '@/graphql/mutations/agency'
import { Email, Password, Required } from '@/utils/rules'

import HeroIcon from '@/components/shared/HeroIcon'
import UserIcon from 'heroicons/24/outline/user.svg'
import EnvelopeIcon from 'heroicons/24/outline/envelope.svg'
import LockIcon from 'heroicons/24/outline/lock-closed.svg'
import KeyIcon from 'heroicons/24/outline/key.svg'
import BuildingIcon from 'heroicons/24/outline/building-office.svg'
import IdentificationIcon from 'heroicons/24/outline/identification.svg'
import PhoneIcon from 'heroicons/24/outline/phone.svg'
import GlobeIcon from 'heroicons/24/outline/globe-alt.svg'
import MapPinIcon from 'heroicons/24/outline/map-pin.svg'
import PhotoIcon from 'heroicons/24/outline/photo.svg'
import ExclamationIcon from 'heroicons/24/outline/exclamation-triangle.svg'

export default {
  data () {
    return {
      UserIcon,
      EnvelopeIcon,
      LockIcon,
      KeyIcon,
      BuildingIcon,
      IdentificationIcon,
      PhoneIcon,
      GlobeIcon,
      MapPinIcon,
      PhotoIcon,
      ExclamationIcon,
      valid: true,
      password: null,
      error: {
        active: false,
        message: ""
      },
      agency: {
        logo: null,
      },
      user: {
        password: null
      },
      emailRules: [ ...Email ],
      requiredRules: [ ...Required ],
      passwordRules: [ ...Password ]
    }
  },

  props: {
    currentUser: {
      required: true,
      type: Object
    }
  },

  computed: {
    canEditAgency () {
      return this.currentUser.displayRoles.includes('gendev') ||
        this.currentUser.displayRoles.includes('admin')
    }
  },

  mounted () {
    this.user = { ...this.currentUser }
    this.agency = { ...this.currentUser.selectedAgency }
  },

  methods: {
    imageUrl (url) {
      return process.env.VUE_APP_RAILS_URL + url
    },

    editUser () {
      if (this.valid) this.valid = this.$refs.user.validate()

      if (this.valid) {
        let attributes = { ...this.user }
        delete attributes.id
        delete attributes.__typename
        delete attributes.selectedAgency
        delete attributes.userAgencies

        this.$apollo.mutate({
          mutation: EditUser,
          variables: {
            input: {
              id: this.agency.id,
              password: this.password,
              attributes: attributes,
            }
          }
        }).then( res => {
          this.$router.go()
        }).catch( err => {
          var error = err.graphQLErrors[0]
          if (error) {
            this.error.message = this.$t('error.' + error.extensions.field + '.' + error.extensions.message)
            this.error.active = true
          }
        })
      }
    },

    editAgency () {
      this.valid = this.$refs.agency.validate()

      if (this.valid) {
        // Only send fields that exist on AgencyInput. selectedAgency carries
        // read-only fields (e.g. unseenInquiries, logoUrl, __typename) that the
        // mutation rejects, so build the payload from an explicit whitelist.
        const a = this.agency
        let attributes = {
          name: a.name,
          email: a.email,
          phone: a.phone,
          website: a.website,
          address: a.address
        }
        if (a.logo) attributes.logo = a.logo

        this.$apollo.mutate({
          mutation: EditAgency,
          variables: {
            input: {
              id: a.id,
              attributes: attributes
            }
          }
        }).then( res => {
          this.$router.go()
        })
      }
    },
  },

  components: { HeroIcon }
}
</script>

<style scoped>
.set {
  max-width: 880px;
}

/* section cards (mirror staff Detail) */
.set-section {
  background: #fff;
  border: 1px solid #ece7de;
  border-radius: 12px;
  box-shadow: 0 8px 24px -16px rgba(40, 33, 22, 0.18);
  margin-bottom: 16px;
  overflow: hidden;
}

.set-section__head {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 13px 16px;
  border-bottom: 1px solid #ece7de;
  background: #faf8f4;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #756f64;
}
.set-section__head .hero-icon {
  color: #a39a8c;
}

.set-section__body {
  padding: 16px;
}

/* field labels */
.set-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  font-weight: 600;
  color: #756f64;
  margin-bottom: 5px;
}
.set-label .hero-icon {
  color: #a39a8c;
}

.set-hint {
  font-size: 12px;
  color: #a39a8c;
  margin: 6px 0 0;
}

/* inline error banner */
.set-error {
  display: flex;
  align-items: center;
  gap: 9px;
  background: #fdecea;
  color: #b52118;
  border: 1px solid #f4cbc6;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 14px;
}
.set-error .hero-icon {
  color: #b52118;
  flex: none;
}

/* agency logo */
.set-logo-row {
  display: flex;
  gap: 16px;
  align-items: stretch;
}
.set-logo {
  flex: none;
  width: 160px;
  border: 1px solid #ece7de;
  border-radius: 10px;
  background: #faf8f4;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.set-logo__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 10px;
}
.set-logo__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  color: #a39a8c;
  font-size: 12.5px;
  padding: 28px 0;
}
.set-logo-field {
  flex: 1 1 auto;
  min-width: 0;
}

@media (max-width: 600px) {
  .set-logo-row {
    flex-direction: column;
  }
  .set-logo {
    width: 100%;
    min-height: 130px;
  }
}

/* footer */
.set-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 14px;
}

/* rounded outlined fieldsets */
.set ::v-deep .v-text-field--outlined fieldset,
.set ::v-deep .v-input--is-focused fieldset {
  border-radius: 10px;
}
</style>
